import {
    InteractionType,
    acquireAccessToken,
    useAzureAuth,
    MsalAuthenticationResult,
    MsalAuthenticationTemplate,
} from "@datamole/wds-azure-auth";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";

import { Loading } from "@/components/Loading";
import { config } from "@/config";
import { loadAccessToken, storeAccessToken } from "@/features/auth/auth.utils";

/**
 * Set preferred interaction type for token acquisition
 * Choose Redirect or Popup
 */
const INTERACTION_TYPE = InteractionType.Redirect;

type Props = {
    children: ReactNode;
};

export const AuthenticationGuard = ({ children }: Props) => {
    const { instance, isInteractionInProgress } = useAzureAuth({
        interactionType: INTERACTION_TYPE,
    });

    /**
     * Replace component token state with global token state from your redux store or context API.
     */
    const [accessToken, setAccessToken] = useState<string | null>(loadAccessToken());

    const acquireAndPersistAccessToken = useCallback(async () => {
        const accessToken = await acquireAccessToken(
            {
                scopes: [config.AZURE_AD_AUTH_SCOPE],
            },
            instance,
        );

        /**
         * Replace with your own token persistence logic
         */
        setAccessToken(accessToken);
        storeAccessToken(accessToken);
    }, [instance]);

    /**
     * Component to render if any error occurs, i.e. if user cancels the flow
     * You typically want to init login again, redirect to login page, etc.
     */
    const ErrorComponent = ({ error }: MsalAuthenticationResult) => {
        console.error(error?.errorMessage);
        return <Navigate to={"/"} />;
    };

    useEffect(() => {
        /**
         * If user is already logged in or interaction is in progress, do nothing
         */
        if (accessToken || isInteractionInProgress) return;

        acquireAndPersistAccessToken();
    }, [accessToken, isInteractionInProgress, acquireAndPersistAccessToken]);

    return (
        <MsalAuthenticationTemplate
            interactionType={INTERACTION_TYPE}
            errorComponent={ErrorComponent}
            loadingComponent={Loading}
        >
            {children}
        </MsalAuthenticationTemplate>
    );
};
